'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { ComparisonTable } from '@/app/sell/components/ComparisonTable';
import { Faq } from '@/app/sell/components/Faq';
import { PromoAndSteps } from '@/app/sell/components/PromoAndSteps';
import { InputFormData, VinInput } from '@/app/sell/components/ui/VinInput';
import { DropdownMenuOptionType } from '@/components/molecules/DropdownMenu/DropdownMenu';
import { useAuth } from '@/hooks/useAuth';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { AuthDialog } from '@/components/organisms/AuthDialog/AuthDialog';
import { capitalizeWords } from '@/lib/utils';
import { showToast } from '@/lib/toast';
import { EMPTY_VEHICLE_BODY } from '@/lib/constants';
import {
  useGetSavedVehicleInfo,
  useSaveVehicleInfoMutation,
} from '@/hooks/queries/sell-my-car';
import Breadcrumb from '@/components/molecules/Breadcrumb';
import { ResponsiveWrapper } from '@/components/atoms/ResponsiveWrapper/ResponsiveWrapper';

interface SellMyCarTemplateProps {
  allMakes: DropdownMenuOptionType[];
  allModels: Record<string, string[]>;
}

export const SellMyCarTemplate: React.FC<SellMyCarTemplateProps> = ({
  allMakes,
  allModels,
}) => {
  const router = useRouter();
  const { user } = useAuth();
  const vinInputRef = useRef<HTMLDivElement>(null);
  const heroSectionRef = useRef<HTMLDivElement>(null);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [formMode, setFormMode] = useState<'vin' | 'makeAndModel'>('vin');
  const [formData, setFormData] = useState<InputFormData>({
    vin: '',
    make: '',
    model: '',
    year: '',
  });
  const [backGroundImageHeight, setBackGroundImageHeight] =
    useState<string>('');
  const [authDialogOpen, setAuthDialogOpen] = useState<boolean>(false);
  const [invalidVinErrorMessage, setInvalidVinErrorMessage] =
    useState<string>('');
  const resetForm = () =>
    setFormData({ vin: '', make: '', model: '', year: '' });
  const updateFormData = (changes: Partial<InputFormData>) => {
    setFormData((prev) => ({ ...prev, ...changes }));
  };

  const { mutateAsync: saveVehicleInfo } = useSaveVehicleInfoMutation();
  const { refetch: checkIsExistingVin } = useGetSavedVehicleInfo(
    formData.vin,
    null,
    false
  );

  const toggleFormMode = async () => {
    setFormMode(formMode === 'vin' ? 'makeAndModel' : 'vin');
    resetForm();
    setInvalidVinErrorMessage('');
  };

  const fetchVehicleInfoByVin = async (vinNumber: string) => {
    try {
      const response = await fetch(
        `/api/inventory/listings/get_info_by_vin/?vin=${vinNumber}`
      );
      if (response.ok) {
        const data = await response.json();
        if (data !== 'Invalid VIN') {
          return data;
        }
        setInvalidVinErrorMessage('Invalid VIN number');
      }
    } catch (error) {
      showToast('There was a problem fetching vehicle. Please try again.', {
        type: 'error',
      });
    }
    return null;
  };

  const validateVin = (vinNumber: string) => {
    const isValidVin = /^[a-zA-Z0-9]{15,18}$/.test(vinNumber);
    if (!isValidVin) {
      setInvalidVinErrorMessage(
        "Please enter a VIN number that's 15-18 alphanumeric characters"
      );
      return false;
    }
    setInvalidVinErrorMessage('');
    return true;
  };

  const redirectToForm = useCallback(
    async (isExistingVin?: boolean) => {
      if (isExistingVin) {
        sessionStorage.setItem('privateSellVin', formData.vin);
      } else {
        const savedVehicleInfo = {
          ...EMPTY_VEHICLE_BODY,
          Year: formData.year,
          Make: formData.make,
          Model: formData.model,
          Vin: formData.vin,
          MainImage: '',
          UserId: user?.email || '',
          ContactInfo: {
            ...EMPTY_VEHICLE_BODY.ContactInfo,
            Name: user?.fullName || '',
            Email: user?.email || '',
            Phone: user?.phone || '',
          },
        };
        const privateSellVin = await saveVehicleInfo(savedVehicleInfo);
        if (privateSellVin) {
          sessionStorage.setItem('privateSellVin', privateSellVin.vin);
        }
      }
      if (!user) {
        setAuthDialogOpen(true);
        setSubmitted(false);
        return;
      }
      router.push('/sell/forms');
    },
    [
      formData.make,
      formData.model,
      formData.vin,
      formData.year,
      router,
      saveVehicleInfo,
      user,
    ]
  );

  const onSubmit = async () => {
    setSubmitted(true);
    if (formMode === 'vin') {
      if (!validateVin(formData.vin)) {
        setSubmitted(false);
        return;
      }

      const { data: getVehicleInfo } = await checkIsExistingVin();

      if (getVehicleInfo) {
        redirectToForm(true);
        return;
      }

      const vehicleData = await fetchVehicleInfoByVin(formData.vin);
      if (vehicleData) {
        updateFormData({
          year: vehicleData.Year,
          make: capitalizeWords(vehicleData.Make),
          model: vehicleData.Model,
        });
        setSubmitted(false);
      }
    } else {
      redirectToForm();
    }
  };

  useEffect(() => {
    if (vinInputRef.current) {
      setBackGroundImageHeight(`${vinInputRef.current.clientHeight + 80}`);
    }
  }, [vinInputRef, formMode]);

  useEffect(() => {
    if (
      !submitted &&
      formData.make &&
      formData.model &&
      formData.year &&
      formData.vin
    ) {
      redirectToForm();
    }
  }, [
    submitted,
    formData.make,
    formData.model,
    formData.year,
    formData.vin,
    redirectToForm,
  ]);

  const handleScrollToVin = () => {
    if (heroSectionRef.current) {
      heroSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <div className="flex flex-col">
        <section className="bg-white py-s">
          <ResponsiveWrapper>
            <Breadcrumb
              links={[
                {
                  text: 'Home',
                  href: '/',
                },
                {
                  text: 'Sell My Car',
                  href: '/sell',
                },
              ]}
            />
          </ResponsiveWrapper>
        </section>
        <div
          className="relative flex flex-1 flex-col bg-white ml:p-[40px]"
          ref={heroSectionRef}
        >
          <Image
            priority
            src="/images/sell-my-car/header-desktop.webp"
            alt="Header image"
            className="hidden w-full flex-1 rounded-medium object-cover ml:flex"
            style={{ minHeight: `${backGroundImageHeight}px` }}
            width={2733}
            height={824}
          />
          <Image
            priority
            src="/images/sell-my-car/header-mobile.webp"
            alt="Header image"
            className="flex w-full flex-1 ml:hidden"
            width={390}
            height={200}
          />
          <h2 className="absolute left-[20px] top-[40px] block text-h2SemiBold text-brandMidnight ml:hidden">
            Sell My Car
          </h2>
          <div
            className="flex w-full flex-1 ml:absolute ml:left-[80px] ml:top-[60px] ml:max-w-[474px]"
            ref={vinInputRef}
          >
            <VinInput
              onSubmit={onSubmit}
              toggleFormMode={toggleFormMode}
              formMode={formMode}
              formData={formData}
              updateFormData={updateFormData}
              allMakes={allMakes}
              allModels={allModels}
              invalidVinErrorMessage={invalidVinErrorMessage}
              submitted={submitted}
            />
          </div>
        </div>
        <PromoAndSteps handleScrollToVin={handleScrollToVin} />
        <div className="hidden ml:block">
          <ComparisonTable />
        </div>
        <div className="flex flex-col bg-gradient-to-r from-lightTheme-start to-lightTheme-end ml:flex-row ml:gap-[40px] ml:px-[60px] ml:py-[40px]">
          <div className="ml:basis-3/5">
            <Faq />
          </div>
          <div className="ml:basis-2/5">
            <VinInput
              alternate
              onSubmit={onSubmit}
              toggleFormMode={toggleFormMode}
              formMode={formMode}
              formData={formData}
              updateFormData={updateFormData}
              allMakes={allMakes}
              allModels={allModels}
              invalidVinErrorMessage={invalidVinErrorMessage}
              submitted={submitted}
            />
          </div>
        </div>
      </div>
      <AuthDialog
        isAuthDialogOpen={authDialogOpen}
        setIsAuthDialogOpen={setAuthDialogOpen}
        title="We can sell your car!"
        description="Get offers from verified dealers today."
        anchor="/forms"
      />
    </>
  );
};
